import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { organisationsAPI } from '../../../../organisations/api/organisationsAPI';
import { useActivitySection } from '../../../hooks/useActivitySection';
import ActivitySection from './ActivitySection';
import ScoreBar from './ScoreBar';
import { Collapse } from './ScoreReadout';
import SequenceChart from './charts/SequenceChart';
import SectionState from './SectionState';
import { Comments, Composer, usePieceComments } from './PieceComments';
import { ShowMore, usePagedItems } from './usePagedItems';
import {
  attemptKindLabel, bandLabel, caseTypeLabel, dayMonth, decisionLine, evidenceSourceLabel,
  optionLetter, plural, ratioPercent, scoringChange, skillTrend, trimNumber, unscoredLabel,
} from './lib/activityFormat';

/**
 * The id of a written answer's view, which the Feedback tab links to: a
 * comment on a case answer stores the answer (case_study_attempts.id), not
 * the case, so the link names the answer and the section finds its card.
 */
export const caseAnswerAnchor = (attemptId) => `case-answer-${attemptId}`;

/** The id of a case's card. */
export const caseCardId = (caseId) => `case-${caseId}`;

const BANDS = ['strong', 'adequate', 'developing', 'weak'];
const BAND_TONE = { strong: 's', adequate: 'a', developing: 'a', weak: 'w' };
const BAND_PILL = { strong: 'g', adequate: 'a', developing: 'a', weak: 'r' };

/** "3 strong · 2 adequate · 1 not scored": the band counts in words, in band order. */
function bandCounts(list) {
  const counts = BANDS.map((b) => [bandLabel(b).toLowerCase(), list.filter((x) => x === b).length]);
  const unscored = list.filter((x) => !BANDS.includes(x)).length;
  return [...counts, ['not scored', unscored]].filter(([, n]) => n).map(([label, n]) => `${n} ${label}`).join(' · ');
}

const option = (ref) => `${optionLetter(ref.index)} · ${ref.text}`;

const Decision = ({ decision }) => {
  if (!decision) return <p className="td-act-meta">No option chosen.</p>;
  return (
    <dl className="td-act-kv">
      <dt>Chose</dt>
      <dd>
        {option(decision.chosen)}
        {decision.chosen_is_best ? <span className="td-act-pill td-act-pill--g">Best option</span> : null}
      </dd>
      {decision.best && decision.chosen_is_best === false ? (
        <>
          <dt>Best option</dt>
          <dd>
            {option(decision.best)}
            <span className="td-act-pill td-act-pill--r">Not chosen</span>
          </dd>
        </>
      ) : null}
      {decision.why ? (<><dt>Why it matters</dt><dd>{decision.why}</dd></>) : null}
    </dl>
  );
};

const Certainty = ({ written }) => {
  if (!written.certainty) return null;
  const agreed = written.certainty_agreement != null
    ? ` Two independent marking passes agreed on ${ratioPercent(written.certainty_agreement)} of the reasoning.` : '';
  return (
    <p className="td-act-meta">
      {'Marking certainty: '}
      <b>{written.certainty === 'high' ? 'high' : 'low, treat as indicative'}</b>
      {`.${agreed}`}
    </p>
  );
};

const Written = ({
  written, open, showEarlier, commentedHere, onToggleAnswer, onToggleEarlier,
}) => {
  if (!written) return <p className="td-act-meta">No written answer yet.</p>;
  const m = written.match;
  const earlier = written.earlier_attempts;
  const viewId = caseAnswerAnchor(written.attempt_id);
  const earlierId = `case-earlier-${written.attempt_id}`;
  return (
    <>
      {m ? (
        <>
          <ScoreBar label={`Overall match (${(bandLabel(m.band) || 'not scored').toLowerCase()})`} value={m.total} outOf={1} display={ratioPercent(m.total)} />
          <ScoreBar label="Key steps covered" value={m.key_steps_covered} outOf={m.key_steps_total || 1} display={`${m.key_steps_covered}/${m.key_steps_total}`} />
          {m.links_total != null ? (
            <ScoreBar label="Links between steps" value={m.links_reproduced} outOf={m.links_total || 1} display={`${m.links_reproduced}/${m.links_total}`} />
          ) : null}
          <ScoreBar label="Emphasis" value={m.emphasis} outOf={1} display={ratioPercent(m.emphasis)} />
          <ScoreBar label="Order of reasoning" value={m.order} outOf={1} display={ratioPercent(m.order)} />
        </>
      ) : <p className="td-act-meta">This answer has no reasoning match.</p>}
      {written.missed_steps.length ? (
        <>
          <div className="td-act-meta">{written.missed_steps.length === 1 ? 'Missed step' : 'Missed steps'}</div>
          <div className="td-act-miss">
            {written.missed_steps.map((s) => <span key={s} className="td-act-pill td-act-pill--r">{s}</span>)}
          </div>
        </>
      ) : null}
      <Certainty written={written} />
      <div className="td-act-btnrow">
        <button
          type="button"
          className="td-btn td-btn--small"
          aria-expanded={open.includes(written.attempt_id)}
          aria-controls={viewId}
          onClick={() => onToggleAnswer(written.attempt_id)}
        >
          {open.includes(written.attempt_id) ? 'Hide answer' : 'Read answer'}
        </button>
        {earlier.length ? (
          <button
            type="button"
            className="td-btn td-btn--small"
            aria-expanded={showEarlier}
            aria-controls={earlierId}
            onClick={onToggleEarlier}
          >
            {`Earlier answers (${earlier.length})`}
          </button>
        ) : null}
      </div>
      {earlier.length && showEarlier ? (
        <ul id={earlierId} className="td-act-rows" aria-label="Earlier answers to this case">
          {earlier.map((e) => (
            <li key={e.attempt_id} className="td-act-rowline">
              <div className="td-act-rowline__main">
                <div className="td-act-rowtitle">{`Answer of ${dayMonth(e.completed_at)}`}</div>
                <div className="td-act-rowsub">
                  {e.has_comment || commentedHere.has(e.attempt_id)
                    ? <span className="td-act-pill td-act-pill--g">Commented</span>
                    : <span className="td-act-pill td-act-pill--b">Not yet</span>}
                </div>
              </div>
              <button
                type="button"
                className="td-btn td-btn--small"
                aria-expanded={open.includes(e.attempt_id)}
                aria-controls={caseAnswerAnchor(e.attempt_id)}
                aria-label={`${open.includes(e.attempt_id) ? 'Hide' : 'Read'} the answer of ${dayMonth(e.completed_at)}`}
                onClick={() => onToggleAnswer(e.attempt_id)}
              >
                {open.includes(e.attempt_id) ? 'Hide' : 'Read'}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
};

const AnswerBody = ({ studentId, answer, onCommented }) => {
  const item = { kind: 'case_study', id: answer.attempt_id };
  const thread = usePieceComments(studentId, item, answer.comments, onCommented);
  const told = answer.verdict || answer.missed_from_case.length || answer.how_to_fix.length;
  let noFeedback = null;
  if (!told) {
    noFeedback = answer.has_feedback
      ? 'The feedback stored for this answer cannot be shown.'
      : 'This answer has not been marked yet.';
  }
  return (
    <div className="td-act-detail-grid">
      <div>
        <div className="td-stat__eyebrow td-act-label">Scores (0–10)</div>
        {answer.scores.length ? answer.scores.map((s) => (
          <ScoreBar key={s.key} label={s.label} value={s.value} outOf={s.out_of} />
        )) : <p className="td-act-meta">No scores were stored for this answer.</p>}
        {answer.mark ? (
          <p className="td-act-meta">
            {'Mark: '}
            <b>{`${trimNumber(answer.mark.value)} / ${answer.mark.out_of}`}</b>
          </p>
        ) : null}
      </div>
      <div>
        <div className="td-stat__eyebrow td-act-label">Verdict the student saw</div>
        {answer.verdict ? <p className="td-act-quote">{answer.verdict}</p> : null}
        {noFeedback ? <p className="td-act-meta">{noFeedback}</p> : null}
        {answer.missed_from_case.length ? (
          <>
            <div className="td-stat__eyebrow td-act-label">Missed from the case</div>
            <ul className="td-act-points">{answer.missed_from_case.map((p) => <li key={p}>{p}</li>)}</ul>
          </>
        ) : null}
        {answer.how_to_fix.length ? (
          <>
            <div className="td-stat__eyebrow td-act-label">How to fix it</div>
            <ul className="td-act-points">{answer.how_to_fix.map((p) => <li key={p}>{p}</li>)}</ul>
          </>
        ) : null}
        <Comments comments={thread.comments} label="Comments on this answer" />
        {answer.has_feedback && !thread.composing ? (
          <div className="td-act-btnrow">
            <button type="button" className="td-btn td-btn--confirm td-btn--small" onClick={thread.startComposing}>
              Comment on this answer
            </button>
          </div>
        ) : null}
        {answer.has_feedback && thread.composing ? (
          <Composer
            noun="answer"
            posting={thread.posting}
            error={thread.postError}
            onSubmit={thread.post}
            onCancel={thread.cancel}
          />
        ) : null}
        {!answer.has_feedback ? (
          <p className="td-act-meta">An answer can take a comment once it has been marked.</p>
        ) : null}
      </div>
      {answer.answer_text.trim() ? (
        <div className="td-act-work">{answer.answer_text}</div>
      ) : <p className="td-act-meta">The answer text is not available.</p>}
    </div>
  );
};

/**
 * One written answer in full, from GET …/activity/cases/written/<attempt_id>:
 * the student's text (as text), the five scores, the verdict, what it missed
 * and how to fix it, the comments on it and a composer.
 */
export const WrittenAnswer = ({ studentId, attemptId, onCommented }) => {
  const fetchAnswer = useCallback(
    (sid) => organisationsAPI.getStudentCaseWrittenAnswer(sid, attemptId), [attemptId],
  );
  const state = useActivitySection(fetchAnswer, studentId, attemptId);
  const title = state.status === 'ready'
    ? `Written answer · ${dayMonth(state.data.completed_at)}${state.data.word_count ? ` · ${plural(state.data.word_count, 'word')}` : ''}`
    : 'Written answer';
  return (
    <div id={caseAnswerAnchor(attemptId)} className="td-act-transcript">
      <div className="td-act-subhead">{title}</div>
      <SectionState state={state} what="the written answer" />
      {state.status === 'ready' ? (
        <AnswerBody studentId={studentId} answer={state.data} onCommented={onCommented} />
      ) : null}
    </div>
  );
};

/** The student's own tutorial responses or discussion turns, from GET …/activity/cases/<attempt_id>. */
export const CaseAttemptView = ({ studentId, attemptId, id }) => {
  const fetchAttempt = useCallback((sid) => organisationsAPI.getStudentCaseAttempt(sid, attemptId), [attemptId]);
  const state = useActivitySection(fetchAttempt, studentId, attemptId, { isEmpty: (d) => !d.items.length });
  return (
    <div id={id} className="td-act-transcript">
      <SectionState state={state} what="the responses" emptyTitle="No responses were recorded" />
      {state.status === 'ready' ? (
        <ol className="td-act-turns" aria-label={`${attemptKindLabel(state.data.kind) || 'Responses'}: the student's own words`}>
          {state.data.items.map((item) => (
            <li key={item.number} className="td-act-turn td-act-turn--candidate">
              <span className="td-act-turn__speaker">
                {`${item.number}. `}
                <span className={`td-act-pill td-act-pill--${BAND_PILL[item.band] || 'o'}`}>
                  {bandLabel(item.band) || 'Not scored'}
                </span>
              </span>
              <p className="td-act-turn__text">{item.text}</p>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
};

const Steps = ({ bands, what }) => (
  <>
    <div className="td-act-steps" aria-hidden="true">
      {bands.map((b, i) => (
        // Bands are positional: the nth response's band.
        // eslint-disable-next-line react/no-array-index-key
        <i key={i} className={BAND_TONE[b] || ''} />
      ))}
    </div>
    <div className="td-act-rowsub">{`${what}: ${bandCounts(bands) || 'none scored'}`}</div>
  </>
);

const AttemptRow = ({ title, meta, bands, what, attemptId, studentId }) => {
  const [open, setOpen] = useState(false);
  const viewId = `case-attempt-${attemptId}`;
  return (
    <li className="td-act-run">
      <div className="td-act-rowline">
        <div className="td-act-rowline__main">
          <div className="td-act-rowtitle">{title}</div>
          <div className="td-act-rowsub">{meta}</div>
          {bands.length ? <Steps bands={bands} what={what} /> : null}
        </div>
        <button type="button" className="td-btn td-btn--small" aria-expanded={open} aria-controls={viewId} onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Read'}
        </button>
      </div>
      {open ? <CaseAttemptView studentId={studentId} attemptId={attemptId} id={viewId} /> : null}
    </li>
  );
};

const AfterWritten = ({ entry, studentId }) => {
  const { quiz, tutorial, discussion } = entry;
  return (
    <ul className="td-act-rows">
      <li className="td-act-rowline">
        <div className="td-act-rowline__main">
          <div className="td-act-rowtitle">Quiz</div>
          <div className="td-act-rowsub">{quiz ? `${quiz.correct} of ${quiz.total} correct` : 'Not started'}</div>
        </div>
        {quiz && quiz.total ? <span className="td-act-mark">{ratioPercent(quiz.correct / quiz.total)}</span> : null}
      </li>
      {tutorial ? (
        <AttemptRow
          title="Tutorial"
          meta={tutorial.questions_total != null
            ? `${tutorial.responses} of ${plural(tutorial.questions_total, 'question')} answered`
            : plural(tutorial.responses, 'response')}
          bands={tutorial.response_bands}
          what="Match per response"
          attemptId={tutorial.attempt_id}
          studentId={studentId}
        />
      ) : (
        <li className="td-act-rowline"><div className="td-act-rowline__main"><div className="td-act-rowtitle">Tutorial</div><div className="td-act-rowsub">Not started</div></div></li>
      )}
      {discussion ? (
        <AttemptRow
          title="Discussion with the case's stakeholders"
          meta={plural(discussion.turns, 'turn')}
          bands={discussion.turn_bands}
          what="Match per turn"
          attemptId={discussion.attempt_id}
          studentId={studentId}
        />
      ) : (
        <li className="td-act-rowline"><div className="td-act-rowline__main"><div className="td-act-rowtitle">Discussion</div><div className="td-act-rowsub">Not started</div></div></li>
      )}
    </ul>
  );
};

/** The case's answer ids a link may open: the latest, then the earlier ones. */
const answerIds = (entry) => (entry.written
  ? [entry.written.attempt_id, ...entry.written.earlier_attempts.map((e) => e.attempt_id)] : []);

const CaseCard = ({ entry, studentId, linkedAnswer, onCommentPosted }) => {
  const meta = [entry.subject, caseTypeLabel(entry.case_type), dayMonth(entry.last_activity_at)].filter(Boolean).join(' · ');
  const linked = answerIds(entry).includes(linkedAnswer) ? linkedAnswer : null;
  // Open answers, in the order they were opened; a linked answer starts open.
  const [open, setOpen] = useState(() => (linked ? [linked] : []));
  const [showEarlier, setShowEarlier] = useState(() => Boolean(linked) && linked !== entry.written?.attempt_id);
  const toggleAnswer = (id) => setOpen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  // Answers commented on here, so an earlier answer's row reads Commented at once.
  const [commentedHere, setCommentedHere] = useState(() => new Set());
  const onCommented = (item, comment) => {
    setCommentedHere((prev) => new Set(prev).add(item.id));
    if (onCommentPosted) onCommentPosted(item, comment);
  };
  return (
    <article id={caseCardId(entry.case_id)} className="td-card td-act-case">
      <div className="td-act-case__head">
        <h4 className="td-card__title">{entry.title}</h4>
        <span className="td-act-meta">{meta}</span>
      </div>
      <div className="td-act-case__grid">
        <div>
          <div className="td-act-subhead">Decision</div>
          <Decision decision={entry.decision} />
          <div className="td-act-subhead td-act-gap-sm">Written answer vs the model reasoning</div>
          <Written
            written={entry.written}
            open={open}
            showEarlier={showEarlier}
            commentedHere={commentedHere}
            onToggleAnswer={toggleAnswer}
            onToggleEarlier={() => setShowEarlier((v) => !v)}
          />
        </div>
        <div>
          <div className="td-act-subhead">After the written answer</div>
          <AfterWritten entry={entry} studentId={studentId} />
          {entry.evidence.length ? (
            <>
              <div className="td-act-subhead td-act-gap-sm">In the student&apos;s words</div>
              {entry.evidence.map((e) => (
                <blockquote key={e.quote} className="td-act-evidence">
                  {`“${e.quote}”`}
                  <small>{[`Shows: ${e.shows}`, evidenceSourceLabel(e.source)].filter(Boolean).join(' · ')}</small>
                </blockquote>
              ))}
            </>
          ) : null}
        </div>
      </div>
      {open.map((id) => (
        <WrittenAnswer key={id} studentId={studentId} attemptId={id} onCommented={onCommented} />
      ))}
    </article>
  );
};

const ofPair = (part) => (part ? `${part.correct} of ${part.answered}` : null);

const Chip = ({ value }) => {
  if (!value) return <span className="td-act-meta">—</span>;
  if (value.unscored) return <span>{unscoredLabel(value.unscored) || null}</span>;
  const band = bandLabel(value.band);
  return <span>{band ? `${value.score} ${band}` : value.score}</span>;
};

const PartLine = ({ card }) => {
  const parts = card?.parts || {};
  const written = parts.written;
  const quiz = parts.quiz;
  const tutorial = parts.tutorial;
  const discussion = parts.discussion;
  const quizBits = quiz ? [ofPair(quiz.reasoning) && `reasoning ${ofPair(quiz.reasoning)}`, ofPair(quiz.knowledge) && `knowledge ${ofPair(quiz.knowledge)}`].filter(Boolean) : [];
  return (
    <div className="td-act-scorecard__parts">
      <p>{written?.unscored ? (unscoredLabel(written.unscored) || 'Not scored') : written ? `Written answer: steps ${written.steps_covered} of ${written.steps_total} · concepts ${written.concepts_covered} of ${written.concepts_total}` : 'Written answer: Not started'}</p>
      <p>{quiz ? (quizBits.length ? `Quiz: ${quizBits.join(' · ')}` : 'Quiz') : 'Quiz: Not started'}</p>
      <p>{tutorial ? `Tutorial: links explained ${tutorial.links_explained} of ${tutorial.links_answered} · supporting concepts covered ${tutorial.concepts_covered} of ${tutorial.concepts_total}` : 'Tutorial: Not started'}</p>
      <p>{discussion ? `Discussion: ${discussion.strong_or_adequate} of ${discussion.turns}. Indicative only` : 'Discussion: Not started'}</p>
    </div>
  );
};

const Scorecard = ({ card, entry, studentId, linkedAnswer, onCommentPosted }) => {
  const linked = Boolean(linkedAnswer && answerIds(entry).includes(linkedAnswer));
  const [open, setOpen] = useState(linked);
  useEffect(() => {
    if (linked) setOpen(true);
  }, [linked]);
  const missed = [...(card?.missed_steps || []), ...(card?.missed_concepts || [])];
  const line = decisionLine(card);
  return (
    <div className="td-act-scorecard">
      <p className="td-act-subhead">{entry.title}</p>
      <p className="td-act-meta">{[entry.subject, dayMonth(entry.last_activity_at)].filter(Boolean).join(' · ')}</p>
      <p>Reasoning <Chip value={card?.reasoning} /></p>
      <p>Knowledge <Chip value={card?.knowledge} /></p>
      {line ? <p>{line}</p> : null}
      <PartLine card={card} />
      {missed.length ? <p>{`Missed: ${missed.join(', ')}`}</p> : null}
      <Collapse
        id={`case-details-${entry.case_id}`}
        open={open}
        onToggle={() => setOpen((value) => !value)}
        label="Answer and details"
      >
        <CaseCard entry={entry} studentId={studentId} linkedAnswer={linkedAnswer} onCommentPosted={onCommentPosted} />
      </Collapse>
    </div>
  );
};

const Skills = ({ skills }) => (
  <div className="td-card td-act-gap td-act-skills">
    <h4 className="td-card__title">Skills across cases</h4>
    {skills && skills.length ? (
      <table>
        <thead>
          <tr><th>Skill</th><th>Type</th><th>Shown</th><th>Trend</th><th>Last tested</th></tr>
        </thead>
        <tbody>
          {skills.map((skill) => (
            <tr key={`${skill.type}-${skill.label}`}>
              <td>{skill.label}</td>
              <td>{skill.type === 'knowledge' ? 'Knowledge' : 'Reasoning'}</td>
              <td>{`shown ${skill.shown} of ${skill.tested}`}</td>
              <td>{skillTrend(skill.trend) || null}</td>
              <td>{skill.last_tested ? dayMonth(skill.last_tested) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : <p className="td-act-meta">None yet.</p>}
  </div>
);

const MeasureCard = ({ title, measure }) => (
  <div className="td-card td-act-score">
    <h4 className="td-card__title">{title}</h4>
    {measure && measure.current != null ? (
      <>
        <div className="td-act-score__head">
          <span className="td-act-score__n">{measure.current}</span>
          {bandLabel(measure.band) ? <span className="td-act-score__band">{bandLabel(measure.band)}</span> : null}
        </div>
        <SequenceChart points={measure.points} />
        {scoringChange(measure.change) ? <p className="td-act-meta">{scoringChange(measure.change)}</p> : null}
        <p className="td-act-meta">Average of the last 3 cases</p>
        {measure.latest ? <p className="td-act-meta">{`${measure.latest.covered} of ${measure.latest.total} · ${measure.latest.case}`}</p> : null}
      </>
    ) : <p className="td-act-meta">No score yet</p>}
    <p className="td-act-note">A guide, not a mark.</p>
  </div>
);

/**
 * Cases, from GET …/activity/cases: each case end to end (decision, how the
 * written answer matched the model reasoning, the answer itself and the
 * earlier answers to it, then the quiz, tutorial and discussion), with the
 * concepts card across every case.
 *
 * A written answer whose anchor is in the URL's hash opens, and is
 * scrolled to, when the list arrives, so a link from the Feedback tab lands
 * on it. An answer beyond the list's first page (or outside the period) is
 * not opened. `onCommentPosted(item, comment)` is told about a comment
 * posted on an answer.
 */
const CasesSection = ({ state, studentId, fetchPage, onCommentPosted }) => {
  const first = state.status === 'ready' ? state.data : null;
  const [casesOpen, setCasesOpen] = useState(false);
  const seenStudent = useRef(studentId);
  useEffect(() => {
    if (seenStudent.current === studentId) return;
    seenStudent.current = studentId;
    setCasesOpen(false);
  }, [studentId]);
  const paged = usePagedItems(first, 'cases', fetchPage);
  // Read once per first page, before its cards mount, so the linked card mounts open.
  const hash = typeof window === 'undefined' ? '' : window.location.hash;
  const linkedAnswer = useMemo(() => {
    if (!first) return null;
    const target = hash.slice(1);
    return first.cases.flatMap(answerIds).find((a) => caseAnswerAnchor(a) === target) || null;
  }, [first, hash]);

  useEffect(() => {
    if (linkedAnswer) setCasesOpen(true);
  }, [linkedAnswer]);

  useEffect(() => {
    if (!linkedAnswer) return;
    const view = document.getElementById(caseAnswerAnchor(linkedAnswer));
    if (view && typeof view.scrollIntoView === 'function') view.scrollIntoView({ block: 'start' });
  }, [linkedAnswer, casesOpen]);

  return (
    <ActivitySection
      id="cases"
      title="Cases"
      description="Each case end to end: the option chosen, how closely the written answer followed the model reasoning, and the quiz, tutorial and discussion after it."
    >
      <SectionState state={state} what="cases" emptyTitle="No case work in this period" />
      {first && first.scoring ? (
        <>
          <div className="td-act-grid td-act-grid--2 td-act-case-scores">
            <MeasureCard title="Reasoning" measure={first.scoring.reasoning} />
            <MeasureCard title="Knowledge" measure={first.scoring.knowledge} />
          </div>
          <div className="td-act-grid td-act-facts">
            <p><b>Best option chosen</b> {first.scoring.decisions.chosen} of {first.scoring.decisions.total}</p>
            <p><b>Cases finished</b> {first.scoring.finished.complete} of {first.scoring.finished.total}</p>
            <p>
              <b>Quiz</b> {first.scoring.quiz.correct} of {first.scoring.quiz.answered}
              {` · reasoning ${first.scoring.quiz.reasoning.correct} of ${first.scoring.quiz.reasoning.answered} · knowledge ${first.scoring.quiz.knowledge.correct} of ${first.scoring.quiz.knowledge.answered}`}
            </p>
            <p><b>Not scored</b> {first.scoring.unscored.count}</p>
          </div>
          <Skills skills={first.scoring.skills} />
          <Collapse
            id="td-act-cases-list"
            open={casesOpen}
            onToggle={() => setCasesOpen((open) => !open)}
            label={`Show all ${first.summary.cases} cases`}
          >
            {paged.items.map((c) => (
              <Scorecard
                key={c.case_id}
                card={(first.scoring.cards || []).find((item) => item.case_id === c.case_id)}
                entry={c}
                studentId={studentId}
                linkedAnswer={linkedAnswer}
                onCommentPosted={onCommentPosted}
              />
            ))}
            <ShowMore paged={paged} what="cases" />
          </Collapse>
        </>
      ) : null}
    </ActivitySection>
  );
};

export default CasesSection;
